<template>
  <div class="center-wrapper">
    <div v-show="!finished">
      <b-form class="form-wrapper mt-5" novalidate @submit.prevent="onSubmit">
        <h5 class="font-weight-bold">{{ $t('membershipForm.changePassword') }}</h5>
        <p class="gray-font">{{ $t('membershipForm.changePasswordSecondary') }}</p>
        <form-error :errors="formErrors" />
        <!-- password -->
        <b-form-group :label="$t('membershipForm.newPassword')" label-for="password">
          <t-input-password
            id="password"
            :value="form.password"
            required
            :maxlength="255"
            :state="$v.form.password.$dirty ? !$v.form.password.$error : null"
            @input="handlePasswordChange"
          />
          <p
            v-show="$v.form.password.$dirty && $v.form.password.$error"
            class="invalid-feedback feedback-display"
          >
            {{ $t('membershipForm.passwordError') }}
          </p>
        </b-form-group>
        <!-- End of password -->

        <!-- confirm password -->
        <b-form-group :label="$t('membershipForm.confirmNewPassword')" label-for="password-confirm">
          <t-input-password
            id="password-confirm"
            :value="form.passwordConfirm"
            :maxlength="255"
            required
            :state="$v.form.passwordConfirm.$dirty ? !$v.form.passwordConfirm.$error : null"
            @input="handlePasswordConfirmChange"
          />
          <p
            v-show="$v.form.passwordConfirm.$dirty && $v.form.passwordConfirm.$error"
            class="invalid-feedback feedback-display"
          >
            {{ $t('membershipForm.passwordError') }}
          </p>
        </b-form-group>
        <!-- End of password -->
        <b-button class="w-100" :disabled="loading" variant="primary-dark" type="submit">
          <b-spinner
            v-show="loading"
            :label="$t('base.loading')"
            class="spinner mr-2"
            small
          ></b-spinner>
          {{ $t('membershipForm.setPassword') }}
        </b-button>
      </b-form>
    </div>
    <div v-show="finished" class="result-wrapper">
      <form-error :errors="formErrors" />
      <Result>
        <template #content>
          <p class="result-primary-text">{{ $t('membershipForm.changePasswordResult') }}</p>
          <p class="result-success-text">{{ $t('membershipForm.changePasswordResult02') }}</p>
          <p class="result-secondary-text">{{ $t('membershipForm.changePasswordResult03') }}</p>
          <b-button
            :to="{ name: paths.membership.signIn, query: { code: setting.code } }"
            variant="primary-dark"
            type="submit"
            block
          >
            {{ $t('base.login') }}
          </b-button>
        </template>
      </Result>
    </div>
  </div>
</template>

<script>
import { required, minLength, sameAs } from 'vuelidate/lib/validators'
import Membership from 'api/Membership'
import FormError from 'components/FormError'
import headerToggleMixin from 'mixins/headerToggleMixin'
import paths from 'routes/paths'
import Result from 'components/Membership/Result'
import _debounce from 'lodash/debounce'
import { sendDataToParentWindow } from 'utilities/domOperation'
import { mapGetters } from 'vuex'
import TInputPassword from 'components/Common/TInputPassword'

export default {
  name: 'ChangePassword',
  components: {
    FormError,
    Result,
    TInputPassword
  },
  mixins: [headerToggleMixin],
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (!vm.token) {
        vm.$router.push({
          name: paths.membership.signIn,
          query: { code: this.setting.code }
        })
      }
    })
  },
  props: {
    displayHeader: {
      type: Boolean,
      default: true
    },
    token: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      finished: false,
      form: {
        password: null,
        passwordConfirm: null
      },
      formErrors: [],
      paths
    }
  },
  computed: {
    ...mapGetters({
      setting: 'setting/getSetting'
    })
  },
  methods: {
    onSubmit() {
      this.$v.form.$touch()
      if (this.$v.$invalid) return
      this.loading = true
      Membership.updatePassword({
        reset_password_token: this.token,
        password: this.form.password
      })
        .then(() => {
          this.finished = true
          this.loading = false
          this.formErrors = []
          sendDataToParentWindow({
            action: 'removeQuery',
            query: 'tripla_booking_widget_open'
          })
        })
        .catch((err) => {
          this.formErrors = err.titleList
          this.loading = false
        })
    },
    // Debounce to reduce the number of js that needs to run everytime users type in a new character.
    // Usually we would not need to do this. However, for some hotels with heavy scripts (see https://umami-me.atlassian.net/browse/CON-10776), this could be useful
    handlePasswordChange: _debounce(function (value) {
      this.form.password = value
    }, 500),
    handlePasswordConfirmChange: _debounce(function (value) {
      this.form.passwordConfirm = value
    }, 500)
  },
  validations: {
    form: {
      password: {
        required,
        minLength: minLength(8)
      },
      passwordConfirm: {
        sameAsPassword: sameAs('password')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~stylesheets/vars.scss';
@import '~stylesheets/mixins.scss';

@include media-breakpoint-up(md) {
  .result-wrapper {
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
